import {
  LayoutDashboard,
  Wifi,
  Printer,
  HelpCircle,
  CalendarDays,
  MessageSquare,
  Map,
  Receipt,
  Activity,
  Settings,
  type LucideIcon,
} from "lucide-react";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** In-beta item: visible to beta-program members (profile.is_beta_tester)
   *  and to staff god-mode; hidden for everyone else. */
  beta?: boolean;
  /** Only visible to tenant admins */
  adminOnly?: boolean;
  /** Only visible when the member's operator has printing set up (profile.print_available). */
  needsPrint?: boolean;
  /** Only visible when the member's company has invoices to show (profile.invoices_available;
   *  the backend reports it for tenant admins only). */
  needsInvoices?: boolean;
  /** Always a tab in the mobile bottom bar. Non-primary items collapse into
   *  the "More" sheet on narrow screens (the bar never wraps); wide screens
   *  show everything inline. */
  primary?: boolean;
}

export const navItems: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, primary: true },
  { to: "/meeting-rooms", label: "Meetings", icon: CalendarDays, primary: true },
  { to: "/floorplan", label: "Floorplan", icon: Map, primary: true },
  { to: "/network", label: "Network", icon: Wifi },
  { to: "/print", label: "Print", icon: Printer, needsPrint: true },
  { to: "/invoices", label: "Invoices", icon: Receipt, adminOnly: true, needsInvoices: true },
  { to: "/activity", label: "Activity", icon: Activity },
  { to: "/faq", label: "Office Guide", icon: HelpCircle },
  // A landing page of areas (/settings/<area>), like the Office Guide's categories.
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/support", label: "Support", icon: MessageSquare, primary: true },
];

/** The profile fields nav gating reads (a subset of `CurrentUser`). */
export interface NavUser {
  is_tenant_admin?: boolean;
  is_beta_tester?: boolean;
  print_available?: boolean;
  invoices_available?: boolean;
}

/** The nav items this member may see. `betaEnabled` = beta items are visible
 *  (a beta tester, or staff not viewing as a member). */
export function visibleNavItems(items: NavItem[], user: NavUser | null | undefined, betaEnabled: boolean): NavItem[] {
  return items
    .filter((item) => !item.adminOnly || !!user?.is_tenant_admin)
    .filter((item) => !item.beta || betaEnabled)
    .filter((item) => !item.needsPrint || !!user?.print_available)
    .filter((item) => !item.needsInvoices || !!user?.invoices_available);
}
